import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, MousePointerClick, Users, TrendingUp, LogOut, RefreshCw,
  ExternalLink, Calendar, Mail, Building2, User, MessageSquare, BarChart3,
  Download, Filter, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';
import { useAuth } from '../components/ProtectedRoute';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ className = "h-8" }) => (
  <img src="https://customer-assets.emergentagent.com/job_smartgrid-hub-3/artifacts/huzue581_Nexintell%20logo.png" alt="Nexintell" className={`${className} object-contain`} />
);

const StatCard = ({ title, value, icon: Icon, color = "coral" }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="stat-card">
    <div className="flex items-center justify-between">
      <span className="text-[#a1a1a1] text-sm">{title}</span>
      <div className={`p-2 rounded-lg ${color === 'coral' ? 'bg-[#ff4b2a]/20' : 'bg-emerald-500/20'}`}>
        <Icon className={`h-5 w-5 ${color === 'coral' ? 'text-[#ff4b2a]' : 'text-emerald-400'}`} />
      </div>
    </div>
    <div className="mt-2"><span className="text-3xl font-bold">{value}</span></div>
  </motion.div>
);

const CTATypeBadge = ({ type }) => {
  const colors = { demo_request: 'bg-[#ff4b2a]/20 text-[#ff4b2a]', explore_product: 'bg-blue-500/20 text-blue-400', contact_partnership: 'bg-purple-500/20 text-purple-400' };
  const labels = { demo_request: 'Demo Request', explore_product: 'Explore Product', contact_partnership: 'Partnership' };
  return <span className={`badge ${colors[type] || 'bg-white/10 text-white'}`}>{labels[type] || type}</span>;
};

const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const DateRangePicker = ({ startDate, endDate, onStartChange, onEndChange, onClear }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="btn-secondary text-sm h-9 px-3">
          <Calendar className="h-4 w-4 mr-2" />{startDate ? format(startDate, 'MMM d, yyyy') : 'Start Date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#111111] border-[#262626]" align="start">
        <CalendarComponent mode="single" selected={startDate} onSelect={onStartChange} initialFocus className="bg-[#111111]" />
      </PopoverContent>
    </Popover>
    <span className="text-[#a1a1a1]">to</span>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="btn-secondary text-sm h-9 px-3">
          <Calendar className="h-4 w-4 mr-2" />{endDate ? format(endDate, 'MMM d, yyyy') : 'End Date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#111111] border-[#262626]" align="start">
        <CalendarComponent mode="single" selected={endDate} onSelect={onEndChange} initialFocus className="bg-[#111111]" />
      </PopoverContent>
    </Popover>
    {(startDate || endDate) && <Button variant="ghost" size="sm" onClick={onClear} className="text-[#a1a1a1] hover:text-white h-9"><X className="h-4 w-4" /></Button>}
  </div>
);

const ClicksTable = ({ clicks }) => (
  <div className="overflow-x-auto">
    <table className="data-table">
      <thead><tr><th>CTA Name</th><th>Type</th><th>Page Source</th><th>Timestamp</th></tr></thead>
      <tbody>
        {clicks.map((click, idx) => (
          <tr key={click.id || idx}>
            <td className="font-medium">{click.cta_name}</td>
            <td><CTATypeBadge type={click.cta_type} /></td>
            <td className="text-[#a1a1a1]">{click.page_source}</td>
            <td className="text-[#a1a1a1]">{formatDate(click.timestamp)}</td>
          </tr>
        ))}
        {clicks.length === 0 && <tr><td colSpan={4} className="text-center text-[#a1a1a1] py-8">No CTA clicks recorded yet</td></tr>}
      </tbody>
    </table>
  </div>
);

const DemoRequestsTable = ({ requests }) => (
  <div className="space-y-4">
    {requests.map((req, idx) => (
      <motion.div key={req.id || idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="dashboard-card">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2"><User className="h-5 w-5 text-[#ff4b2a]" />{req.name}</h3>
            <p className="text-sm text-[#a1a1a1] mt-1">{req.role}</p>
          </div>
          <span className="badge badge-coral"><Calendar className="h-3 w-3 mr-1" />{formatDate(req.timestamp)}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-[#a1a1a1]"><Mail className="h-4 w-4" /><a href={`mailto:${req.email}`} className="hover:text-white">{req.email}</a></div>
          <div className="flex items-center gap-2 text-[#a1a1a1]"><Building2 className="h-4 w-4" />{req.company}</div>
        </div>
        {req.message && <div className="mt-4 pt-4 border-t border-[#262626]"><div className="flex items-start gap-2 text-[#a1a1a1]"><MessageSquare className="h-4 w-4 mt-1" /><p className="text-sm">{req.message}</p></div></div>}
      </motion.div>
    ))}
    {requests.length === 0 && <div className="text-center text-[#a1a1a1] py-12">No demo requests yet</div>}
  </div>
);

const PIE_COLORS = ['#ff4b2a', '#3b82f6', '#a855f7', '#10b981', '#f59e0b'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getToken = useCallback(() => localStorage.getItem('nexintell_token'), []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('nexintell_token');
    localStorage.removeItem('nexintell_user');
    navigate('/admin', { replace: true });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/admin', { replace: true });
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };
    const params = {};
    if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
    if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');

    try {
      const [analyticsRes, clicksRes, demoRes] = await Promise.all([
        axios.get(`${API}/analytics`, { headers, params }),
        axios.get(`${API}/cta/clicks`, { params }),
        axios.get(`${API}/demo-requests`, { headers, params })
      ]);
      setAnalytics(analyticsRes.data);
      setClicks(clicksRes.data);
      setDemoRequests(demoRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired');
        handleLogout();
        return;
      }
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getToken, handleLogout, startDate, endDate, navigate]);

  // Initial data fetch - only runs once when component mounts
  // Auth is already verified by ProtectedRoute
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when date range changes
  useEffect(() => {
    if (!loading && analytics) {
      setRefreshing(true);
      fetchData();
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => { setRefreshing(true); fetchData(); };

  const handleExport = async (type) => {
    const token = getToken();
    const params = {};
    if (startDate) params.start_date = format(startDate, 'yyyy-MM-dd');
    if (endDate) params.end_date = format(endDate, 'yyyy-MM-dd');
    try {
      const response = await axios.get(`${API}/export/${type}`, { headers: { 'Authorization': `Bearer ${token}` }, params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nexintell_${type}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const pieData = analytics?.clicks_by_type ? Object.entries(analytics.clicks_by_type).map(([name, value]) => ({ name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()), value })) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#ff4b2a] border-t-transparent mx-auto mb-4" />
          <p className="text-[#a1a1a1]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center justify-between px-6 h-16">
          <div className="flex items-center gap-4">
            <a href="/"><Logo className="h-8" /></a>
            <span className="text-[#a1a1a1]">/</span>
            <span className="font-medium">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing} className="text-[#a1a1a1] hover:text-white">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <a href="/" target="_blank" rel="noopener noreferrer" className="nav-link flex items-center gap-1">View Site <ExternalLink className="h-4 w-4" /></a>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm text-[#a1a1a1]">{authUser?.name || 'Admin'}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#a1a1a1] hover:text-[#ff4b2a]"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6 max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">CTA Analytics</h1>
              <p className="text-[#a1a1a1]">Monitor your call-to-action performance</p>
            </div>
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-[#a1a1a1]" />
              <DateRangePicker startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} onClear={() => { setStartDate(null); setEndDate(null); }} />
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard title="Total CTA Clicks" value={analytics?.total_clicks || 0} icon={MousePointerClick} />
            <StatCard title="Demo Requests" value={analytics?.demo_requests_count || 0} icon={Users} color="green" />
            <StatCard title="Conversion Rate" value={`${analytics?.conversion_rate || 0}%`} icon={TrendingUp} />
            <StatCard title="Active CTAs" value={Object.keys(analytics?.clicks_by_type || {}).length} icon={LayoutDashboard} />
          </div>
          
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="dashboard-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#ff4b2a]" /> Clicks Over Time</h3>
              <div className="h-64">
                {analytics?.clicks_by_day?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.clicks_by_day}>
                      <defs><linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ff4b2a" stopOpacity={0.3}/><stop offset="95%" stopColor="#ff4b2a" stopOpacity={0}/></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="date" stroke="#a1a1a1" fontSize={12} tickFormatter={(val) => new Date(val).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} />
                      <YAxis stroke="#a1a1a1" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: '#111111', border: '1px solid #262626', borderRadius: '8px', color: '#ededed' }} />
                      <Area type="monotone" dataKey="clicks" stroke="#ff4b2a" fillOpacity={1} fill="url(#colorClicks)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-[#a1a1a1]">No data</div>}
              </div>
            </div>
            <div className="dashboard-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><MousePointerClick className="h-5 w-5 text-[#ff4b2a]" /> Clicks by Type</h3>
              <div className="h-64">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">{pieData.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip contentStyle={{ backgroundColor: '#111111', border: '1px solid #262626', borderRadius: '8px', color: '#ededed' }} /></PieChart>
                  </ResponsiveContainer>
                ) : <div className="h-full flex items-center justify-center text-[#a1a1a1]">No data</div>}
              </div>
              <div className="flex flex-wrap gap-4 mt-4 justify-center">
                {pieData.map((entry, idx) => <div key={entry.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} /><span className="text-sm text-[#a1a1a1]">{entry.name}: {entry.value}</span></div>)}
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="clicks" className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <TabsList className="bg-[#111111] border border-[#262626] p-1">
                <TabsTrigger value="clicks" className="data-[state=active]:bg-[#ff4b2a] data-[state=active]:text-white">CTA Clicks ({clicks.length})</TabsTrigger>
                <TabsTrigger value="requests" className="data-[state=active]:bg-[#ff4b2a] data-[state=active]:text-white">Demo Requests ({demoRequests.length})</TabsTrigger>
              </TabsList>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport('clicks')} className="btn-secondary text-sm"><Download className="h-4 w-4 mr-2" />Export Clicks</Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('demo-requests')} className="btn-secondary text-sm"><Download className="h-4 w-4 mr-2" />Export Requests</Button>
              </div>
            </div>
            <TabsContent value="clicks"><div className="dashboard-card"><h3 className="font-semibold mb-4 flex items-center gap-2"><MousePointerClick className="h-5 w-5 text-[#ff4b2a]" /> Recent CTA Clicks</h3><ScrollArea className="h-[400px]"><ClicksTable clicks={clicks} /></ScrollArea></div></TabsContent>
            <TabsContent value="requests"><div className="dashboard-card"><h3 className="font-semibold mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#ff4b2a]" /> Demo Requests</h3><ScrollArea className="h-[500px]"><DemoRequestsTable requests={demoRequests} /></ScrollArea></div></TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}
